import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Brand, DemoBadge } from "@/components/Brand";
import { ScoreRing, PriorityBadge } from "@/components/Score";
import {
  ArrowRight, Search, Crosshair, Ban, MessageSquare, Target, ShieldCheck,
  Gauge, Layers, Star, MapPin, TrendingUp, Quote, Check,
} from "lucide-react";

const PREVIEW = [
  { rank: 1, name: "Glam Studio", area: "Koregaon Park, Pune", score: 91, label: "HIGH PRIORITY", rating: 4.6, reviews: 287, service: "WhatsApp Booking Automation", value: "₹45K–₹90K", dont: "SEO", angle: "Make it easier for existing customers to book." },
  { rank: 2, name: "Spice Route Kitchen", area: "Bandra West, Mumbai", score: 86, label: "HIGH PRIORITY", rating: 4.3, reviews: 512, service: "Website Development", value: "₹55K–₹1.6L", dont: "Branding", angle: "Turn strong demand into online orders." },
  { rank: 3, name: "Bright Smile Dental", area: "Indiranagar, Bengaluru", score: 76, label: "MEDIUM PRIORITY", rating: 4.0, reviews: 68, service: "Appointment Automation", value: "₹55K–₹1.6L", dont: "Paid Ads", angle: "Competitors already take bookings online." },
];

function Feature({ icon: Icon, title, children }) {
  return (
    <div className="lf-card p-6 lf-rise">
      <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/25 grid place-items-center mb-4">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h3 className="font-display font-semibold text-slate-100 mb-1.5">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{children}</p>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background text-slate-200">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-800/70 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#how" className="hover:text-slate-100 transition-colors">How it works</a>
            <a href="#features" className="hover:text-slate-100 transition-colors">Intelligence</a>
            <a href="#trust" className="hover:text-slate-100 transition-colors">Methodology</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="nav-login-btn" onClick={() => navigate("/login")}>Sign in</Button>
            <Button className="bg-primary hover:bg-emerald-600 text-white" data-testid="nav-cta-btn" onClick={() => navigate("/login")}>
              Find my best clients
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden lf-radial">
        <div className="absolute inset-0 lf-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="lf-rise">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-primary bg-primary/10 border border-primary/25 rounded-full px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary lf-live-dot" />
              AI client-acquisition intelligence
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 leading-[1.05]">
              Find the businesses<br />that need you <span className="text-primary">most.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              LeadForge AI discovers local businesses, finds their growth gaps, tells you what to sell, and gives you the smartest reason to reach out.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="bg-primary hover:bg-emerald-600 text-white h-12 px-6 text-base" data-testid="hero-cta-btn" onClick={() => navigate("/login")}>
                Find my best clients <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-6 text-base border-slate-700 hover:bg-slate-800 text-slate-200" data-testid="hero-demo-btn" onClick={() => document.getElementById("preview")?.scrollIntoView({ behavior: "smooth" })}>
                See how it works
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary" /> No API keys needed</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary" /> Live demo included</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary" /> India-first</span>
            </div>
          </div>

          {/* Product preview */}
          <div id="preview" className="lf-rise" style={{ animationDelay: ".12s" }}>
            <div className="lf-card lf-glow p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Your top opportunities</p>
                  <p className="font-display font-semibold text-slate-100">Pune · Salons & Local Services</p>
                </div>
                <DemoBadge />
              </div>
              <div className="space-y-3">
                {PREVIEW.map((p) => (
                  <div key={p.rank} className="rounded-xl bg-slate-900/60 border border-slate-800 p-3.5 flex items-center gap-3.5">
                    <ScoreRing score={p.score} size={56} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-primary">#{p.rank}</span>
                        <span className="font-display font-semibold text-slate-100 text-sm truncate">{p.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                        <MapPin className="w-3 h-3" />{p.area}
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400 ml-1" />{p.rating} · {p.reviews}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[11px] text-slate-300 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-primary" />{p.service}</span>
                        <span className="text-[11px] font-semibold text-emerald-400 ml-auto">{p.value}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500 px-1">
                <span>Ranked by opportunity, readiness & fit</span>
                <span className="flex items-center gap-1 text-primary"><Crosshair className="w-3 h-3" /> 10 analysed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contrast strip */}
      <section className="border-y border-slate-800/70 bg-slate-900/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-2 gap-8">
          <div className="flex items-start gap-3">
            <Ban className="w-5 h-5 text-slate-600 mt-1 shrink-0" />
            <div>
              <p className="text-slate-500 text-sm">This is not</p>
              <p className="font-display text-lg text-slate-400">"Here are 100 businesses."</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Target className="w-5 h-5 text-primary mt-1 shrink-0" />
            <div>
              <p className="text-primary text-sm">This is</p>
              <p className="font-display text-lg text-slate-100">"Here are the 10 worth your time — and exactly what to sell."</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-mono uppercase tracking-widest text-primary mb-3">How it works</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-50">From market to conversation in one click.</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { n: "01", t: "Set your agency", d: "Tell us what you sell, where, and your typical deal size." },
            { n: "02", t: "Find my best clients", d: "We analyse the market for the strongest commercial gaps." },
            { n: "03", t: "See your top 10", d: "Ranked by opportunity, readiness, competitive gap and fit." },
            { n: "04", t: "Reach out like a human", d: "Get the brief, the angle, and a message worth sending." },
          ].map((s) => (
            <div key={s.n} className="lf-card p-6">
              <span className="font-mono text-sm text-primary">{s.n}</span>
              <h3 className="font-display font-semibold text-slate-100 mt-3">{s.t}</h3>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-mono uppercase tracking-widest text-primary mb-3">The intelligence</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-50">A sales strategist that actually did the research.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Feature icon={Gauge} title="Opportunity Score /100">Deterministic scoring from real signals — website gaps, conversion friction, competitive position and service fit.</Feature>
          <Feature icon={Layers} title="What to sell (and not)">A clear primary service, a secondary, and an explicit "don't pitch" so you never waste a conversation.</Feature>
          <Feature icon={ShieldCheck} title="Evidence layer">Every recommendation separates fact, inference and estimate — so you can trust it and defend it.</Feature>
          <Feature icon={Search} title="Competitive gap">Benchmarks each business against a nearby competitor on reviews, website and booking.</Feature>
          <Feature icon={Crosshair} title="AI Opportunity Brief">The signature card: the opportunity, the evidence, the value, the angle, and your next action.</Feature>
          <Feature icon={MessageSquare} title="Human outreach">WhatsApp, email, DM, LinkedIn and call scripts that sound like a human who did their homework.</Feature>
        </div>
      </section>

      {/* Trust / methodology */}
      <section id="trust" className="border-y border-slate-800/70 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-3">Methodology & trust</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-slate-50 mb-4">Legitimate public data. No fabrication.</h2>
            <ul className="space-y-3 text-sm text-slate-400">
              {[
                "Uses legitimate business data providers, never private accounts.",
                "Deterministic analysis is separated from AI reasoning.",
                "Confidence and freshness shown for every intelligence area.",
                "When data is unavailable, we say so — never invent findings.",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2.5"><Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />{x}</li>
              ))}
            </ul>
          </div>
          <div className="lf-card p-6">
            <Quote className="w-8 h-8 text-primary/40 mb-3" />
            <p className="font-display text-xl text-slate-100 leading-snug">"LeadForge doesn't just find leads. It tells you who to contact, what to sell, why they need it, what it's worth, and exactly how to start the conversation."</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-50">Stop searching for leads.<br />Start finding opportunities.</h2>
        <Button size="lg" className="mt-8 bg-primary hover:bg-emerald-600 text-white h-12 px-8 text-base" data-testid="footer-cta-btn" onClick={() => navigate("/login")}>
          Find my best clients <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <Brand size="sm" />
          <div className="flex flex-wrap items-center gap-6 text-xs text-slate-500">
            <Link to="/legal/privacy" className="hover:text-slate-300">Privacy Policy</Link>
            <Link to="/legal/terms" className="hover:text-slate-300">Terms</Link>
            <Link to="/legal/data-sources" className="hover:text-slate-300">Data Sources</Link>
            <Link to="/legal/methodology" className="hover:text-slate-300">Methodology</Link>
          </div>
          <p className="text-xs text-slate-600">© 2026 LeadForge AI · Built for Emergent Builder Fest</p>
        </div>
      </footer>
    </div>
  );
}
